import { functions } from "../database/firebase";

type OpenAIMethod = "GET" | "POST" | "DELETE";

type OpenAIJsonRequest = {
  path: string;
  method?: OpenAIMethod;
  body?: unknown;
};

type OpenAIUploadTextFileRequest = {
  fileName: string;
  content: string;
  mimeType?: string;
  purpose?: string;
};

const callWithTimeout = async <T>(
  functionName: string,
  payload: unknown,
  timeout: number = 120000
): Promise<T> => {
  const callable = functions.httpsCallable(functionName, { timeout });
  const result = await callable(payload);
  return result.data as T;
};

export const requestOpenAIJson = async <T = any>({
  path,
  method = "POST",
  body,
  timeout,
}: OpenAIJsonRequest & { timeout?: number }): Promise<T> =>
  callWithTimeout<T>("openAIJsonRequest", {
    path,
    method,
    body,
  }, timeout);

export const createOpenAIChatCompletion = async <T = any>(
  body: unknown,
  timeout?: number
): Promise<T> =>
  requestOpenAIJson<T>({
    path: "/chat/completions",
    method: "POST",
    body,
    timeout,
  });

export const createOpenAIResponse = async <T = any>(
  body: unknown
): Promise<T> =>
  requestOpenAIJson<T>({
    path: "/responses",
    method: "POST",
    body,
  });

export const createOpenAIImageGeneration = async <T = any>(
  body: unknown,
  timeout: number = 180000
): Promise<T> =>
  callWithTimeout<T>(
    "openAIJsonRequest",
    {
      path: "/images/generations",
      method: "POST",
      body,
    },
    timeout
  );

type OpenAIImageEditPayload = {
  model: string;
  prompt: string;
  images: string[];
  size?: string;
  quality?: string;
  n?: number;
  output_format?: string;
};

export const createOpenAIImageEdit = async <T = any>(
  payload: OpenAIImageEditPayload,
  timeout: number = 240000
): Promise<T> => callWithTimeout<T>("openAIImageEdit", payload, timeout);

export const uploadOpenAITextFile = async <T = any>(
  file: File,
  purpose: string = "assistants"
): Promise<T> => {
  const payload: OpenAIUploadTextFileRequest = {
    fileName: file.name,
    mimeType: file.type || "text/plain",
    content: await file.text(),
    purpose,
  };

  return callWithTimeout<T>("openAIUploadTextFile", payload, 180000);
};
